import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const MIME_EXTENSION_MAP = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png'
};

const sanitizeNamePart = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'unknown';
  return raw
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '_');
};

const getExtensionFromName = (value) => {
  if (!value) return '';
  const cleanValue = String(value).split('?')[0].split('#')[0];
  const idx = cleanValue.lastIndexOf('.');
  if (idx === -1) return '';
  return cleanValue.slice(idx + 1).toLowerCase();
};

const getExtensionFromDisposition = (contentDisposition) => {
  if (!contentDisposition) return '';
  const match = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  if (!match || !match[1]) return '';
  return getExtensionFromName(decodeURIComponent(match[1].replace(/"/g, '')));
};

function BackendDashboard({ user }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, [filterStatus]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/documents/all`;
      if (filterStatus) {
        url += `?status=${filterStatus}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setLoading(false);
    }
  };

  const handleVerify = async (docId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/documents/${docId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Document verified successfully!');
      fetchDocuments();
    } catch (err) {
      alert(err.response?.data?.message || 'Verification failed');
    }
  };

  const handleReject = async (docId) => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/documents/${docId}/reject`, 
        { rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Document rejected successfully!');
      setShowRejectForm(false);
      setRejectionReason('');
      setSelectedDoc(null);
      fetchDocuments();
    } catch (err) {
      alert(err.response?.data?.message || 'Rejection failed');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const contentType = response.headers?.['content-type'] || '';
      const contentDisposition = response.headers?.['content-disposition'] || '';
      const extension =
        getExtensionFromName(doc.file_name || doc.filename || doc.original_name || doc.path) ||
        getExtensionFromDisposition(contentDisposition) ||
        MIME_EXTENSION_MAP[contentType.toLowerCase()] ||
        'pdf';
      const employeeUserId = sanitizeNamePart(
        response.headers?.['x-employee-userid'] ||
        response.headers?.['X-Employee-UserId'] ||
        doc.employee_user_id ||
        doc.employee_id
      );
      const documentType = sanitizeNamePart(doc.document_type);
      const downloadName = `${employeeUserId}_${documentType}.${extension}`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/documents/export/documents`,
        { status: filterStatus || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.url) {
        window.location.href = `${API_URL}${response.data.url}`;
      }
      alert(`Exported ${response.data.stats.total} documents`);
    } catch (err) {
      alert('Export failed');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  const stats = {
    total: documents.length,
    uploaded: documents.filter(d => d.status === 'uploaded').length,
    verified: documents.filter(d => d.status === 'verified').length,
    rejected: documents.filter(d => d.status === 'rejected').length
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Document Management</h1>
        <p>Verify and manage employee documents</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Documents</h3>
          <div className="value">{stats.total}</div>
        </div>
        <div className="stat-card warning">
          <h3>Pending Verification</h3>
          <div className="value">{stats.uploaded}</div>
        </div>
        <div className="stat-card success">
          <h3>Verified</h3>
          <div className="value">{stats.verified}</div>
        </div>
        <div className="stat-card danger">
          <h3>Rejected</h3>
          <div className="value">{stats.rejected}</div>
        </div>
      </div>

      <div className="section-card">
        <h2>All Documents</h2>
        <div className="btn-group" style={{ marginBottom: '20px' }}>
          <button className="btn btn-primary" onClick={() => setFilterStatus('')}>All</button>
          <button className="btn btn-secondary" onClick={() => setFilterStatus('uploaded')}>Pending</button>
          <button className="btn btn-success" onClick={() => setFilterStatus('verified')}>Verified</button>
          <button className="btn btn-danger" onClick={() => setFilterStatus('rejected')}>Rejected</button>
          <button className="btn btn-primary" onClick={handleExport}>Export to Excel</button>
        </div>

        {selectedDoc && showRejectForm && (
          <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#fff3cd', borderRadius: '5px' }}>
            <h3>Reject Document</h3>
            <div className="form-group">
              <label>Rejection Reason:</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Provide a detailed reason for rejection..."
                rows="4"
              />
            </div>
            <div className="btn-group">
              <button className="btn btn-danger" onClick={() => handleReject(selectedDoc.id)}>
                Confirm Rejection
              </button>
              <button className="btn btn-secondary" onClick={() => {
                setShowRejectForm(false);
                setSelectedDoc(null);
                setRejectionReason('');
              }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {documents.length === 0 ? (
          <p style={{ marginTop: '20px', color: '#999' }}>No documents found.</p>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Employee Name</th>
                  <th>Email</th>
                  <th>Document Type</th>
                  <th>Document #</th>
                  <th>Status</th>
                  <th>Uploaded Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => (
                  <tr key={doc.id}>
                    <td>{doc.employee_name}</td>
                    <td>{doc.email}</td>
                    <td>{doc.document_type}</td>
                    <td>{doc.document_number || '-'}</td>
                    <td>
                      <span className={`badge ${doc.status}`}>
                        {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                      </span>
                    </td>
                    <td>{new Date(doc.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="btn-group" style={{ gap: '5px' }}>
                        <button 
                          className="btn btn-primary"
                          onClick={() => handleDownload(doc)}
                          style={{ padding: '5px 10px', fontSize: '12px' }}
                        >
                          Download
                        </button>
                        {doc.status === 'uploaded' && (
                          <>
                            <button 
                              className="btn btn-success"
                              onClick={() => handleVerify(doc.id)}
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                            >
                              Verify
                            </button>
                            <button 
                              className="btn btn-danger"
                              onClick={() => {
                                setSelectedDoc(doc);
                                setShowRejectForm(true);
                              }}
                              style={{ padding: '5px 10px', fontSize: '12px' }}
                            >
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default BackendDashboard;
